'use client'

import type { CalculatorProps, CalculatorVariant } from '@/components/calculator/variants'
import type { IncomeRange } from '@/views/microsite-pf/pf/pfMicrositeShared'
import type { ComponentType, ReactNode } from 'react'

import '../sections.css'

import parse from 'html-react-parser'

import Calculator from '@/components/calculator'
import { CALCULATOR_VARIANTS } from '@/components/calculator/variants'
import useStore from '@/stores/Store'

interface JumbotronPFProps {
  badgeText?: ReactNode
  title: string
  buttonText?: ReactNode
  footNotes?: ReactNode
  subText?: ReactNode
  /** Calculator variant identifier passed through to the calculator slot. */
  source?: string
  /**
   * Optional — server-rendered callers (static pages that don't react to the
   * income toggle) omit it so the view can stay a Server Component. The
   * calculator slot treats it as optional too.
   */
  onIncomeRangeChange?: (range: IncomeRange) => void
  /**
   * Calculator rendered in the right column, chosen by key. **Server Component
   * callers must use this**, not `calculatorComponent`: a component reference
   * is not serializable across the RSC boundary and drags the client module
   * into the server graph (`createContext is not a function` at build time).
   */
  calculatorVariant?: CalculatorVariant
  /**
   * Escape hatch for `'use client'` callers that already hold a component
   * reference (e.g. `PfMicrositeTemplate`'s per-product config). Takes
   * precedence over `calculatorVariant`.
   */
  calculatorComponent?: ComponentType<CalculatorProps>
}

function JumbotronPF({
  badgeText,
  title,
  buttonText,
  footNotes,
  subText,
  source,
  onIncomeRangeChange,
  calculatorVariant,
  calculatorComponent
}: JumbotronPFProps) {
  const { formRef } = useStore()
  const CalculatorComponent =
    calculatorComponent ?? (calculatorVariant ? CALCULATOR_VARIANTS[calculatorVariant] : Calculator)

  return (
    <div className="container">
      <div className="row">
        <div className="col-xl-6 mx-auto">
          <div className="d-flex flex-column align-items-start text-start">
            <div className="jumbotron-title">{badgeText}</div>
            <h1 className="jumbotron-subtitle">{parse(title)}</h1>
            <div className="jumbotron-subtext mb-3">{subText}</div>
            {buttonText && (
              <button
                className="btn btn-primary btn-lg applybtn mb-3 col-xl-5"
                type="button"
                onClick={() => {
                  if (formRef.current) {
                    const offset = 410 // Adjust this value as needed
                    const topPosition =
                      formRef.current.getBoundingClientRect().top + window.pageYOffset - offset
                    window.scrollTo({
                      top: topPosition,
                      behavior: 'smooth'
                    })
                  }
                  // formRef?.current?.scrollIntoView({ block: "end" })
                }}
              >
                {buttonText}
              </button>
            )}
            {footNotes && (
              <div className="jumbotron-note d-flex align-items-center">
                <i className="bi bi-shield me-2"></i>
                {footNotes}
              </div>
            )}
          </div>
        </div>
        <div className="col-xl-6 d-flex justify-content-center px-0">
          <CalculatorComponent onIncomeRangeChange={onIncomeRangeChange} source={source} />
        </div>
      </div>
    </div>
  )
}

export default JumbotronPF
